<template>
    <div data-backdrop="static" data-keyboard="false" class="modal fade" :id="HTMLIDs.modal"
         tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title page-title" v-html="modalTitle"/>
                </div>
                <form @submit.prevent="submit" class="form-horizontal">
                    <div class="modal-body">
                        <div class="form-group row">
                            <label class="col-6 control-label">
                                <span v-html="$t('CarrierDestinationFormModal.country')"/>
                                <RedAsterisk/>
                            </label>
                            <label class="col-6 control-label" v-html="$t('CarrierDestinationFormModal.area')"/>
                            <div class="col-6">
                                <CountrySelect :clearable="false" :required="true" @setSelectedCountry="setSelectedCountry"
                                               :selected-country-alpha3="carrierDestination.country.alpha3ISO"/>
                            </div>
                            <div class="col-6">
                                <VSelect :options="formattedAreas" @input="setSelectedArea" :filter-by="filterBy"
                                          :value="selectedFormattedArea" :clearable="false"/>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label :for="HTMLIDs.inputs.minDeliveryDays" class="col-6 control-label"
                                   v-html="$t('CarrierDestinationFormModal.minDeliveryDays')"/>
                            <label :for="HTMLIDs.inputs.maxDeliveryDays" class="col-6 control-label"
                                   v-html="$t('CarrierDestinationFormModal.maxDeliveryDays')"/>
                            <div class="col-6">
                                <div class="input-group">
                                    <input :id="HTMLIDs.inputs.minDeliveryDays" min="1" :max="maxForMinDeliveryDays"
                                           :placeholder="$t('CarrierDestinationFormModal.optional')" type="number"
                                           class="form-control" v-model="carrierDestination.minDeliveryDays"/>
                                    <div class="input-group-text">
                                        <span class="input-group-append" v-html="$t('CarrierDestinationFormModal.days')"/>
                                    </div>
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="input-group">
                                    <input :id="HTMLIDs.inputs.maxDeliveryDays" :min="minForMaxDeliveryDays" max="100"
                                           :placeholder="$t('CarrierDestinationFormModal.optional')" type="number"
                                           class="form-control" v-model="carrierDestination.maxDeliveryDays"/>
                                    <div class="input-group-text">
                                        <span class="input-group-append" v-html="$t('CarrierDestinationFormModal.days')"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label :for="HTMLIDs.inputs.fixedTaxPercent" class="col-6 control-label">
                                <span v-html="$t('CarrierDestinationFormModal.fixedTaxPercent')"/>
                                <InfoCircle :tooltip="$t('CarrierDestinationFormModal.whatIsFixedTaxPercent')"/>
                            </label>
                            <label :for="HTMLIDs.inputs.margin" class="col-6 control-label">
                                <span v-html="$t('CarrierDestinationFormModal.margin')"/>
                                <InfoCircle :tooltip="$t('CarrierDestinationFormModal.whatIsMargin')"/>
                            </label>
                            <div class="col-6">
                                <div class="input-group">
                                    <input :id="HTMLIDs.inputs.fixedTaxPercent" min="0" max="100"
                                           :placeholder="$t('CarrierDestinationFormModal.optional')" type="number"
                                           class="form-control" step="0.01" v-model="carrierDestination.fixedTaxPercent"/>
                                    <div class="input-group-text">
                                        <span class="input-group-append" v-html="'%'"/>
                                    </div>
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="input-group">
                                    <input :id="HTMLIDs.inputs.margin" min="0" max="100"
                                           :placeholder="$t('CarrierDestinationFormModal.optional')" type="number"
                                           class="form-control" step="0.01" v-model="carrierDestination.margin"/>
                                    <div class="input-group-text">
                                        <span class="input-group-append" v-html="'%'"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label for="carrier-destination-enabledForApi" class="col-6 control-label" v-html="$t('CarrierDestinationFormModal.enabledForApi')"/>
                            <label class="col-6 control-label"></label>
                            <div class="col-6">
                                <div class="input-group">
                                    <toggle-button :value="carrierDestination.enabledForApi" :sync="true" v-model="carrierDestination.enabledForApi"/>
                                </div>
                            </div>
                            <div class="col-6"></div>
                        </div>
                    </div>
                    <div class="modal-footer flex justify-content-between">
                        <button :disabled="loading" type="button" class="btn btn-secondary" data-dismiss="modal"
                                v-html="$t('CarrierDestinationFormModal.close')"/>
                        <SubmitBtn classes="btn btn-primary" :text="modalMainAction" :loading="loading"/>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from "sweetalert2";
import uniqid from "uniqid";
import { mapGetters } from "vuex";
import SubmitBtn from "../../forms/SubmitBtn";
import CarrierDestination from "../../../../classes/Billing/Carrier/CarrierDestination";
import RedAsterisk from "../../forms/RedAsterisk";
import CountrySelect from "../../forms/CountrySelect";
import InfoCircle from "../../misc/InfoCircle";
import { filterBy } from "../../../../helpers/VSelect";

export default {
    name: "CarrierDestinationFormModal",
    components: {InfoCircle, CountrySelect, RedAsterisk, SubmitBtn },
    props: {
        carrier: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            carrierDestination: new CarrierDestination(),
            unchangedCarrierDestination: new CarrierDestination(),
            mode: "create",
            HTMLIDs: {
                modal: `carrier-destination-form-modal-${uniqid()}`,
                inputs: {
                    fixedTaxPercent: `fixed-tax-percent-${uniqid()}`,
                    minDeliveryDays: `min-delivery-days-${uniqid()}`,
                    maxDeliveryDays: `max-delivery-days-${uniqid()}`,
                },
            },
        };
    },
    computed: {
        ...mapGetters("countries", {
            countries: "countries",
        }),
        modalTitle() {
            return this.mode === "create" ? this.$t("CarrierDestinationFormModal.addCarrierDestination") : this.$t("CarrierDestinationFormModal.editCarrierDestination");
        },
        modalMainAction() {
            return this.mode === "create" ? this.$t("CarrierDestinationFormModal.add") : this.$t("CarrierDestinationFormModal.edit");
        },
        formattedAreas() {
            if (this.carrier === undefined) {
                return undefined;
            }
            return [
                { key: null, label: this.$t("CarrierDestinationFormModal.noArea") },
                ...this.carrier.areas.map(area => ({ key: area.id, label: area.name })),
            ];
        },
        selectedFormattedArea() {
            if (this.carrier === undefined || this.carrierDestination.carrierAreaId === undefined) {
                return null;
            }
            return this.formattedAreas.find(area => area.key === this.carrierDestination.carrierAreaId);
        },
        maxForMinDeliveryDays() {
            return this.carrierDestination.maxDeliveryDays && typeof this.carrierDestination.maxDeliveryDays === "number" ? 99 : parseInt(this.carrierDestination.maxDeliveryDays) - 1;
        },
        minForMaxDeliveryDays() {
            return this.carrierDestination.minDeliveryDays && typeof this.carrierDestination.minDeliveryDays === "number" ? 2 : parseInt(this.carrierDestination.minDeliveryDays) + 1;
        },
    },
    methods: {
        show(carrierDestination = null) {
            this.mode = carrierDestination === null ? "create" : "edit";
            this.carrierDestination = new CarrierDestination(carrierDestination);
            this.unchangedCarrierDestination = new CarrierDestination(this.carrierDestination);
            // eslint-disable-next-line no-undef
            $(`#${this.HTMLIDs.modal}`).modal("show");
        },
        async submit() {
            this.carrierDestination.carrierId = this.carrier.id;
            this.carrierDestination.sanitizeForAPI();
            try {
                this.loading = true;
                if (this.mode === "create") {
                    await this.submitCreate();
                    this.$toasted.success(this.$t("CarrierDestinationFormModal.carrierDestinationAdded"), { icon: "check" });
                } else if (this.mode === "edit") {
                    if (this.carrierDestination.carrierAreaId !== this.unchangedCarrierDestination.carrierAreaId &&
                        this.carrierDestination.carrierAreaId !== null) {
                        await this.confirmSubmitEdit();
                    } else {
                        await this.submitEdit();
                    }
                }
            } catch (e) {
                this.$displayError(e);
            } finally {
                this.loading = false;
            }
        },
        async addDestinationInArea(carrierDestinationId) {
            const response = await this.$diceAPI.addCarrierAreaDestination(this.carrierDestination.carrierAreaId, carrierDestinationId);
            this.$emit("editCarrierArea", response.data);
        },
        async editDestinationInArea(carrierDestinationId, carrierAreaId) {
            let response;
            if (carrierAreaId !== null) {
                response = await this.$diceAPI.addCarrierAreaDestination(carrierAreaId, carrierDestinationId);
            } else {
                response = await this.$diceAPI.deleteCarrierAreaDestination(this.unchangedCarrierDestination.carrierAreaId, carrierDestinationId);
            }
            this.$emit("editCarrierArea", response.data);
        },
        setSelectedCountry(country) {
            this.carrierDestination.countryId = !country ? undefined : country.id;
            this.carrierDestination.country.alpha3ISO = !country ? undefined : country.alpha3ISO;
        },
        setSelectedArea(area) {
            this.carrierDestination.carrierAreaId = area === null ? undefined : area.key;
        },
        async submitCreate() {
            let response = await this.$diceAPI.postCarrierDestination(this.carrierDestination);
            if (this.carrierDestination.carrierAreaId !== null) {
                await this.addDestinationInArea(response.data.id);
            }
            response = await this.$diceAPI.getCarrierDestination(response.data.id);
            this.$emit("addCarrierDestination", response.data);
            // eslint-disable-next-line no-undef
            $(`#${this.HTMLIDs.modal}`).modal("hide");
            return response.data;
        },
        async confirmSubmitEdit() {
            const result = await Swal.fire({
                title: this.$t("CarrierDestinationFormModal.youSureToChangeArea"),
                text: this.$t("CarrierDestinationFormModal.pricesWillBeOverwritten"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: this.$t("CarrierDestinationFormModal.edit"),
                cancelButtonText: this.$t("CarrierDestinationFormModal.cancel"),
            });
            if (result.value) {
                await this.submitEdit();
            }
        },
        async submitEdit() {
            let response = await this.$diceAPI.patchCarrierDestination(this.carrierDestination);
            if (this.carrierDestination.carrierAreaId !== this.unchangedCarrierDestination.carrierAreaId) {
                await this.editDestinationInArea(response.data.id, this.carrierDestination.carrierAreaId);
            }
            response = await this.$diceAPI.getCarrierDestination(response.data.id);
            this.$emit("editCarrierDestination", response.data);
            // eslint-disable-next-line no-undef
            $(`#${this.HTMLIDs.modal}`).modal("hide");
            this.$toasted.success(this.$t("CarrierDestinationFormModal.carrierDestinationEdited"), { icon: "check" });
        },
        filterBy,
    },
};
</script>

<style scoped>

</style>